/**
 * Mermaid diagram definitions for the PRE documentation page.
 *
 * Each constant is a static string passed to <MermaidDiagram chart={...} />.
 * They are deliberately kept in one file so a non-engineer can copy-paste
 * any of them into mermaid.live to preview without running the app.
 *
 * Diagram strings must be valid Mermaid syntax (no smart quotes, no
 * trailing whitespace inside node labels).
 */

// 1. End-to-end PRE decision flow
export const DIAGRAM_HOW_IT_WORKS = `flowchart LR
    A([Send Request]) --> B[Resolve Jurisdiction<br/>from phone or country]
    B --> C[Find Applicable Rules<br/>for channel and purpose]
    C --> D{Layer 1<br/>Eligibility}
    D -->|Fails| X([BLOCK])
    D -->|Passes| E{Layer 2<br/>Constraints}
    E -->|Fails| X
    E -->|Conditions| Y([ALLOW WITH<br/>CONDITIONS])
    E -->|Clean Pass| Z([ALLOW])
    X --> L[Write to Decision Log]
    Y --> L
    Z --> L

    style A fill:#e0e7ff,stroke:#6366f1,color:#1e1b4b
    style X fill:#fee2e2,stroke:#ef4444,color:#7f1d1d
    style Y fill:#fef3c7,stroke:#f59e0b,color:#78350f
    style Z fill:#d1fae5,stroke:#10b981,color:#064e3b
    style L fill:#f1f5f9,stroke:#64748b,color:#0f172a`;

// 2. The three layers
export const DIAGRAM_THREE_LAYERS = `flowchart TB
    subgraph L0 ["Layer 0 — Consent Capture"]
        direction LR
        L0A[Contact Created] --> L0B[Auto-grant<br/>Informational where allowed] --> L0C[Seed ConsentRecord]
    end
    subgraph L1 ["Layer 1 — Eligibility"]
        direction LR
        L1A[Send Requested] --> L1B[Check consent for<br/>this channel and purpose] --> L1C[Check Internal DNC] --> L1D[Check National Registries]
    end
    subgraph L2 ["Layer 2 — Constraints"]
        direction LR
        L2A[Eligibility Passed] --> L2B[Check Calling Hours] --> L2C[Check Frequency Caps] --> L2D[Check Content<br/>Compliance]
    end

    L0 -.->|happens once,<br/>at contact creation| L1
    L1 -->|fires per send| L2

    style L0 fill:#ecfdf5,stroke:#10b981
    style L1 fill:#eff6ff,stroke:#3b82f6
    style L2 fill:#fef3c7,stroke:#f59e0b`;

// 3. Jurisdiction inheritance tree
export const DIAGRAM_JURISDICTIONS = `flowchart TB
    GLOBAL([GLOBAL<br/>baseline rules])
    GLOBAL --> USFED[US-FED<br/>United States Federal]
    GLOBAL --> CA([CA — Canada])
    GLOBAL --> BR([BR — Brazil])
    GLOBAL --> MX([MX — Mexico])
    GLOBAL --> OTHER([CO, AR, CL, PE,<br/>and more])

    USFED --> USCA[US-CA<br/>California]
    USFED --> USNY[US-NY<br/>New York]
    USFED --> USTX[US-TX<br/>Texas]
    USFED --> USFL[US-FL<br/>Florida]
    USFED --> USMORE[50 states + DC]

    style GLOBAL fill:#e0e7ff,stroke:#6366f1,color:#1e1b4b
    style USFED fill:#dbeafe,stroke:#3b82f6,color:#1e3a8a
    style USMORE fill:#f1f5f9,stroke:#64748b,color:#334155,stroke-dasharray: 5 5`;

// 4. Rule structure
export const DIAGRAM_RULE_STRUCTURE = `flowchart LR
    subgraph RULE ["A Rule"]
        direction TB
        R1[Layer]
        R2[Channel<br/>SMS / Email / Voice]
        R3[Purpose<br/>Marketing / Informational]
        R4[B2B or B2C]
        R5[Required Checks]
        R6[Effect<br/>Allow / Condition / Block]
    end
    RULE --> EVAL{Evaluator}
    EVAL --> ALLOW([ALLOW])
    EVAL --> COND([ALLOW WITH<br/>CONDITIONS])
    EVAL --> BLOCK([BLOCK])

    style RULE fill:#f5f3ff,stroke:#8b5cf6
    style ALLOW fill:#d1fae5,stroke:#10b981,color:#064e3b
    style COND fill:#fef3c7,stroke:#f59e0b,color:#78350f
    style BLOCK fill:#fee2e2,stroke:#ef4444,color:#7f1d1d`;

// 5. Calling hours timeline
export const DIAGRAM_CALLING_HOURS = `flowchart LR
    H00[00:00] --> H06[06:00]
    H06 --> H08[08:00]
    H08 ==>|PERMITTED| H21[21:00]
    H21 --> H24[24:00]

    style H00 fill:#fee2e2,stroke:#ef4444
    style H06 fill:#fee2e2,stroke:#ef4444
    style H08 fill:#d1fae5,stroke:#10b981
    style H21 fill:#fee2e2,stroke:#ef4444
    style H24 fill:#fee2e2,stroke:#ef4444`;

// 6. Contact creation → Layer 0 sequence
export const DIAGRAM_CONTACT_CREATION = `sequenceDiagram
    participant Op as Operator / API
    participant CS as Contact Service
    participant PRE as PRE Layer 0
    participant CR as ConsentRecord

    Op->>CS: Create contact (name, phone, email)
    CS->>PRE: Evaluate Layer 0 for this contact
    PRE->>PRE: Resolve jurisdiction from phone
    PRE->>PRE: Find auto-grant rules for jurisdiction
    PRE-->>CS: Informational allowed, Marketing pending
    CS->>CR: Seed ConsentRecord rows
    Note over CR: Informational consent auto-granted. Marketing requires explicit opt-in.`;

// 7. Authoring lifecycle — save in the modal → DB → next send → decision log.
//    Used in the Authoring Guide section to show what actually happens when an
//    operator clicks Save. The dotted edge represents the time gap between
//    save and the next inbound send.
export const DIAGRAM_AUTHORING_LIFECYCLE = `flowchart TB
    A([Author opens<br/>Add or Edit Rule]) --> B[Fill fields:<br/>Layer, Effect, Channel,<br/>Purpose, Checks, etc.]
    B --> C{Click Save}
    C --> D[POST or PUT to<br/>api/super-admin/pre/rules]
    D --> E[(PRERule row<br/>written, isActive=true)]
    E --> F[(PRERuleVersion<br/>snapshot created)]
    F --> G[(SuperAdminAuditLog<br/>entry recorded)]
    G --> H([Rule is LIVE])
    H -.->|next message send| I[Delivery worker queries<br/>PRERule.findMany]
    I --> J[buildPREInput<br/>contact and tenant context]
    J --> K{Evaluate}
    K --> L([ALLOW])
    K --> M([ALLOW WITH<br/>CONDITIONS])
    K --> N([BLOCK])
    L --> O[(PreDecisionLog<br/>row written)]
    M --> O
    N --> O

    style A fill:#e0e7ff,stroke:#6366f1,color:#1e1b4b
    style H fill:#fef3c7,stroke:#f59e0b,color:#78350f
    style L fill:#d1fae5,stroke:#10b981,color:#064e3b
    style M fill:#fef3c7,stroke:#f59e0b,color:#78350f
    style N fill:#fee2e2,stroke:#ef4444,color:#7f1d1d
    style O fill:#f1f5f9,stroke:#64748b,color:#0f172a`;

// 8. Layer decision flow — the order the engine evaluates layers in, and how
//    each layer can short-circuit. Layer 1 BLOCK short-circuits the whole
//    pipeline; Layer 2 only runs after Layer 1 passes.
export const DIAGRAM_LAYER_DECISION = `flowchart TB
    A([Send Requested]) --> L1B{Layer 1<br/>BLOCK rules}
    L1B -->|any fails| BLOCK([BLOCK<br/>Layer 2 skipped])
    L1B -->|all pass| L1C[Layer 1<br/>CONDITION rules]
    L1C --> L2B{Layer 2<br/>BLOCK rules}
    L2B -->|any fails| BLOCK
    L2B -->|all pass| L2C[Layer 2<br/>CONDITION rules]
    L2C --> COND{Any conditions<br/>attached?}
    COND -->|yes| AWC([ALLOW WITH<br/>CONDITIONS])
    COND -->|no| ALLOW([ALLOW])

    style A fill:#e0e7ff,stroke:#6366f1,color:#1e1b4b
    style BLOCK fill:#fee2e2,stroke:#ef4444,color:#7f1d1d
    style AWC fill:#fef3c7,stroke:#f59e0b,color:#78350f
    style ALLOW fill:#d1fae5,stroke:#10b981,color:#064e3b`;

// 9. Decision states
export const DIAGRAM_DECISION_STATES = `stateDiagram-v2
    [*] --> Evaluating
    Evaluating --> ALLOW : All checks pass
    Evaluating --> ALLOW_WITH_CONDITIONS : Passes with conditions
    Evaluating --> BLOCK : Any block-effect check fails

    note right of ALLOW
        Message sends immediately.
        Risk flags may still be attached
        as warnings for the audit log.
    end note

    note right of ALLOW_WITH_CONDITIONS
        Conditions must be satisfied
        before sending (eg. wait until
        calling hours, redact PII).
    end note

    note right of BLOCK
        Send is suppressed.
        Remediation suggests what the
        operator or contact must do
        to make a future send eligible.
    end note`;
